import {
  aPIPaths,
  handleResponse,
  methods,
  requestJSON,
  requestWithToken,
  responseTypes
} from "@redux/Config";
import { STORAGE_ITEMS } from "@config";

export const KYCServices = {
  sendKYCApplication,
  getListKYC,
  modifyKYCApplication,
  approveKYCApplication,
  deleteKYCApplication,
  startKYC,
  uploadPowerOfAttorney,
  uploadOrganizationalChart,
  uploadArticlesOfAssociation,
  uploadBeneficiaryDocuments,
  uploadWolfsbergQuestionnaire,
  submitApplication,
  startApplicationSigning,
  checkSigningSca,
  cancelSigning,
  cancelUploadDocument,
  continueApplication,
  checkState,
  startAgreementSigning,
  uploadAdditionalDocuments,
  startOndatoBeneficiaryIdentification,
  uploadApplicationDocument,
  startApplicationSignedUpload,
  uploadSignedApplication,
  uploadSignedAgreement,
  renderPdfFromHtml,
  uploadKycPdf,
  validateData,
  getInstitution,
  getCompanyClassification,
};

function sendKYCApplication(data) {
  return requestJSON(aPIPaths.post_sendKycApplication, {
    method: methods.post,
    data: data
  }).then(handleResponse);
}

function getListKYC(data) {
  return requestJSON(aPIPaths.post_getListKYC, {
    method: methods.post,
    data: data
  }).then(handleResponse);
}

function modifyKYCApplication(data) {
  return requestJSON(aPIPaths.post_modifyKycApplication, {
    method: methods.put,
    data: data
  }).then(handleResponse);
}

function approveKYCApplication(kycId) {
  return requestJSON(aPIPaths.post_approveKycApplication + "?KycId=" + kycId, {
    method: methods.post
  }).then(handleResponse);
}

function deleteKYCApplication(kycId) {
  return requestJSON(aPIPaths.delete_deleteKycApplication + "?KycId=" + kycId, {
    method: methods.delete
  }).then(handleResponse);
}

function startKYC() {
  return requestJSON(aPIPaths.startKYC).then(handleResponse);
}

function uploadPowerOfAttorney(data) {
  return requestJSON(aPIPaths.uploadPowerOfAttorney, {
    data: data,
    method: methods.post
  }).then(handleResponse);
}

function uploadOrganizationalChart(data) {
  return requestJSON(aPIPaths.uploadOrganizationalChart, {
    data: data,
    method: methods.post
  }).then(handleResponse);
}

function uploadApplicationDocument(data, documentId) {
  return requestJSON(
    aPIPaths.uploadApplicationDocument.replace("{applicationDocumentId}", documentId),
    {
      data: data,
      method: methods.post
    }
  ).then(handleResponse);
}

function uploadSignedAgreement(data, documentId) {
  return requestJSON(
    aPIPaths.uploadSignedAgreement.replace("{applicationDocumentId}", documentId),
    {
      data: data,
      method: methods.post
    }
  ).then(handleResponse);
}

function getCompanyClassification({ companyType, sectorCode, naceCode }) {
  return requestJSON(
    aPIPaths.getCompanyClassification.replace("{companyType}", companyType)
      .replace("{institutionalSectorCode}", sectorCode)
      .replace("{naceCode}", naceCode),
    {
      method: methods.get
    }
  ).then(handleResponse);
}

function uploadSignedApplication(data, documentId) {
  return requestJSON(
    aPIPaths.uploadSignedApplication.replace("{applicationDocumentId}", documentId),
    {
      data: data,
      method: methods.post
    }
  ).then(handleResponse);
}

function uploadArticlesOfAssociation(data) {
  return requestJSON(aPIPaths.uploadArticlesOfAssociation, {
    data: data,
    method: methods.post
  }).then(handleResponse);
}

function uploadBeneficiaryDocuments(data) {
  return requestJSON(aPIPaths.uploadBeneficiaryDocuments, {
    data: data,
    method: methods.post
  }).then(handleResponse);
}

function uploadWolfsbergQuestionnaire(data) {
  return requestJSON(aPIPaths.uploadWolfsbergQuestionnaire, {
    data: data,
    method: methods.post
  }).then(handleResponse);
}

function submitApplication(data) {
  return requestJSON(aPIPaths.submitApplication, {
    data: data,
    method: methods.post
  }).then(handleResponse);
}

function startApplicationSigning(authType) {
  return requestJSON(`${aPIPaths.startApplicationSigning}?authType=${authType}`, {
    method: methods.post
  }).then(handleResponse);
}

function cancelUploadDocument() {
  return requestJSON(aPIPaths.cancelUploadDocument, {
    method: methods.get
  }).then(handleResponse);
}

function startApplicationSignedUpload(data) {
  return requestJSON(aPIPaths.startApplicationSigning, {
    method: methods.post,
    data
  }).then(handleResponse);
}

function checkSigningSca() {
  const token = localStorage.getItem(STORAGE_ITEMS.SIGNIN_TOKEN);
  requestWithToken.defaults.headers["Authorization"] = `Bearer ${token}`;
  return requestWithToken(aPIPaths.checkSigningSca).then(handleResponse);
}

function cancelSigning() {
  const token = localStorage.getItem(STORAGE_ITEMS.SIGNIN_TOKEN);
  requestWithToken.defaults.headers["Authorization"] = `Bearer ${token}`;
  return requestWithToken(aPIPaths.cancelSigning).then(handleResponse);
}

function continueApplication() {
  return requestJSON(aPIPaths.continueApplication, {
    method: methods.post
  }).then(handleResponse);
}

function checkState() {
  return requestJSON(aPIPaths.checkState).then(handleResponse);
}

function startAgreementSigning(authType) {
  return requestJSON(`${aPIPaths.startAgreementSigning}?authType=${authType}`, {
    method: methods.post
  }).then(handleResponse);
}

function uploadAdditionalDocuments(data) {
  return requestJSON(aPIPaths.uploadAdditionalDocuments, {
    data: data,
    method: methods.post
  }).then(handleResponse);
}

function startOndatoBeneficiaryIdentification() {
  return requestJSON(aPIPaths.startOndatoBeneficiaryIdentification, {
    method: methods.post
  }).then(handleResponse);
}

function renderPdfFromHtml(htmlStr) {
  return requestJSON(aPIPaths.renderPdfFromHtml, {
    method: methods.post,
    data: `"${htmlStr}"`,
    responseType: responseTypes.arraybuffer
  }).then(handleResponse);
}

function uploadKycPdf(file) {
  return requestJSON(aPIPaths.uploadKycPdf, {
    method: methods.post,
    data: file
  }).then(handleResponse);
}

function validateData(data) {
  return requestJSON(aPIPaths.validateData, {
    method: methods.post,
    data
  }).then(handleResponse);
}

function getInstitution(legalForm, nace) {
  return requestJSON(
    aPIPaths.institutionalCodeMap.replace("{legalForm}", legalForm).replace("{nace}", nace),
    {
      method: methods.get
    }
  ).then(handleResponse);
}
